/*
  ba-cart-sale-note.js
  WHAT: Keeps the cart RRP / Your price / You save summary correct as the cart
        changes, without a full drawer reload.
  WHY:  The note's first render is server-side Liquid, which is right on page
        load. But the drawer footer is NOT re-rendered by templates/cart.ajax.liquid
        - that file replaces only the line items and the cart-level discount row.
        So the commonest path of all (land on a PDP, add to cart, drawer opens)
        would show a figure that predates the item just added.
  WHEN: 2026-10-30.

  WHY A SECOND FETCH: /cart.js does NOT carry compare_at_price. Verified against
  the live store on 2026-10-30 - the line-item keys are price, original_price,
  line_price, original_line_price, final_line_price, total_discount and so on,
  with no compare-at anywhere. The RRP lives in /products/<handle>.js, so the
  sale portion of the saving has to come from there. Results are cached per
  handle for the life of the page; carts here are one to three lines, so this is
  normally one extra request and often zero.

  The discount portion needs no lookup: cart.original_total_price - total_price.

  Event coverage mirrors ba-cart-shipping-bar.js - Impulse emits several names.

  No storage: per CLAUDE.md rule 5, no localStorage / sessionStorage. The handle
  cache is a plain in-memory object and dies with the page.
*/

(function () {
  'use strict';

  var compareCache = {}; // handle -> { variantId: compareAtPriceInCents }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // ALL of them, not the first. On /cart there are TWO: the cart drawer's copy,
  // which the header renders on every page, and the cart page's own. A single
  // querySelector would only ever find the drawer's (it comes first in the DOM),
  // leaving the one the shopper is actually looking at frozen at its server-rendered
  // value after an AJAX quantity change.
  function notes() {
    return document.querySelectorAll('[data-cart-sale-note]');
  }

  // Must match ba-price.liquid's `money_without_trailing_zeros` EXACTLY, or the figure
  // changes shape the moment JS takes over from the server render.
  //
  // "no trailing zeros" drops the cents only when they are zero - $300, but $655.80.
  // Rounding everything to whole dollars looked fine until a cart of 2 x X6 hit
  // $655.80: Liquid printed $655.80, this printed $656, and the number appeared to
  // move on its own after a quantity change. Measured 2026-10-30.
  var fmtWhole = null, fmtCents = null;
  function formatMoney(cents) {
    var whole = cents % 100 === 0;
    try {
      if (whole && !fmtWhole) {
        fmtWhole = new Intl.NumberFormat('en-AU', {
          style: 'currency', currency: 'AUD',
          minimumFractionDigits: 0, maximumFractionDigits: 0
        });
      }
      if (!whole && !fmtCents) {
        fmtCents = new Intl.NumberFormat('en-AU', {
          style: 'currency', currency: 'AUD',
          minimumFractionDigits: 2, maximumFractionDigits: 2
        });
      }
    } catch (e) {
      return '$' + (whole ? Math.round(cents / 100) : (cents / 100).toFixed(2));
    }
    return (whole ? fmtWhole : fmtCents).format(cents / 100);
  }

  function compareMapFor(handle) {
    if (compareCache[handle]) return Promise.resolve(compareCache[handle]);
    return fetch(root() + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) {
        var map = {};
        if (product && product.variants) {
          for (var i = 0; i < product.variants.length; i++) {
            var v = product.variants[i];
            if (v.compare_at_price) map[v.id] = v.compare_at_price;
          }
        }
        compareCache[handle] = map;
        return map;
      })
      // A failed lookup caches an empty map on purpose: the note then reports only
      // the discount portion rather than retrying on every cart event.
      .catch(function () { compareCache[handle] = {}; return {}; });
  }

  function setFigure(root, selector, cents) {
    var el = root.querySelector(selector);
    if (el) el.textContent = formatMoney(cents);
  }

  function refresh() {
    if (!notes().length) return;

    fetch(root() + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return null;

        // Paid lines only. The Gift Box app's $0 lines carry a real compare-at,
        // so counting them would invent a saving on a full-price cart - see the
        // snippet header.
        var paid = (cart.items || []).filter(function (i) { return i.final_line_price > 0; });
        if (!paid.length) return { saved: 0, rrp: 0, payable: 0 };

        var handles = [];
        for (var i = 0; i < paid.length; i++) {
          if (paid[i].handle && handles.indexOf(paid[i].handle) === -1) handles.push(paid[i].handle);
        }

        return Promise.all(handles.map(compareMapFor)).then(function (maps) {
          var byHandle = {};
          for (var h = 0; h < handles.length; h++) byHandle[handles[h]] = maps[h];

          // Same single subtraction as the Liquid: total RRP, minus what the cart
          // actually costs. cart.total_price already has every discount taken off it,
          // so nothing can be counted twice.
          var rrp = 0;
          for (var j = 0; j < paid.length; j++) {
            var line = paid[j];
            var cmp = (byHandle[line.handle] || {})[line.variant_id];
            var unit = (cmp && cmp > line.original_price) ? cmp : line.original_price;
            rrp += unit * line.quantity;
          }

          var payable = cart.total_price || 0;
          return { rrp: rrp, payable: payable, saved: rrp - payable };
        });
      })
      .then(function (result) {
        if (!result) return;
        // Re-query: the drawer may have been re-rendered while those fetches ran.
        var els = notes();
        for (var k = 0; k < els.length; k++) {
          var el = els[k];
          if (result.saved > 0) {
            setFigure(el, '[data-sale-note-rrp]', result.rrp);
            setFigure(el, '[data-sale-note-paid]', result.payable);
            setFigure(el, '[data-sale-note-amount]', result.saved);
            el.hidden = false;
          } else {
            el.hidden = true;
          }
        }
      })
      .catch(function () { /* silent - never break the cart */ });
  }

  function attach() {
    document.addEventListener('cart:updated', refresh);
    document.addEventListener('cart:build', refresh);
    document.addEventListener('cart:refresh', refresh);
    document.addEventListener('ajaxProduct:added', refresh);
    document.addEventListener('theme:cart:change', refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
